import { holdsGranular, levelFor, type ProjectPlacement } from '../app/session'

/** The feedback module's tool code, as the evaluator spells it. */
export const FEEDBACK_TOOL = 'feedback'

/**
 * The levels a granular permission attaches to, as the evaluator has them
 * (`PermissionLevel.accepts_granular`): Read Only and Standard. At None a
 * granular decides nothing, and Admin already has everything.
 */
const ACCEPTS_GRANULAR: ReadonlySet<string> = new Set(['read_only', 'standard'])

/**
 * Whether this person is offered the feedback board on this project.
 *
 * Composed the way the server's triage policy is evaluated: Admin on the
 * Feedback tool, or the "Triage feedback and bug reports" permission held at
 * Read Only or Standard on it. The permission held by somebody at None on the
 * tool offers nothing, because the evaluator does not read it there. Like
 * every copy of the evaluator's numbers in the browser, this decides what is
 * offered and nothing else: the screens answer at their addresses whatever is
 * offered, and the instance answers or refuses each read and move.
 */
export function mayTriage(placement: ProjectPlacement | undefined): boolean {
  const level = levelFor(placement, FEEDBACK_TOOL)
  if (level === 'admin') return true
  return ACCEPTS_GRANULAR.has(level) && holdsGranular(placement, FEEDBACK_TOOL, 'triage')
}

/** The states a report moves through, in the board's filter order. */
export const STATUSES = [
  'new',
  'triaged',
  'in_progress',
  'fixed',
  'verified',
  'closed',
  'wont_fix',
  'duplicate',
] as const

export const KINDS = ['bug', 'suggestion', 'question', 'praise'] as const
export const SEVERITIES = ['critical', 'major', 'minor'] as const
export const PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const

/** The states nothing further is expected from; each can be reopened in its window. */
export const TERMINAL: ReadonlySet<string> = new Set(['verified', 'closed', 'wont_fix', 'duplicate'])

/**
 * What may follow what, copied from the module's transition table
 * (`lifecycle.py`). Used to offer only the buttons the server would accept;
 * the server still decides, and a refusal is shown as it comes back.
 */
export const NEXT: Record<string, readonly string[]> = {
  new: ['triaged', 'wont_fix', 'duplicate'],
  triaged: ['in_progress', 'fixed', 'wont_fix', 'duplicate'],
  in_progress: ['fixed', 'wont_fix', 'duplicate'],
  fixed: ['verified', 'closed', 'in_progress'],
  verified: [],
  closed: [],
  wont_fix: [],
  duplicate: [],
}
